import { Icon } from '@iconify/react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { useApi } from '../hooks/useApi'
import { CloseBtn, H1, Overlay, Panel, Section, TitleRow } from '../styles/TransactionDetailStyle'
import ConfirmPinModal from './ConfirmPinModal'
import ConfirmSendMoneyModal from './ConfirmSendMoneyModal'

interface SendMoneySidebarProps {
   open: boolean
   onClose: () => void
}

type UserItem = {
   id: string
   email: string
   username?: string | null
}

export default function SendMoneySidebar({ open, onClose }: SendMoneySidebarProps) {
   const [openUsers, setOpenUsers] = useState(false)
   const usersBtnRef = useRef<HTMLButtonElement | null>(null)
   const [query, setQuery] = useState('')
   const [selected, setSelected] = useState<UserItem[]>([])
   const [selectAll, setSelectAll] = useState(false)
   const [amount, setAmount] = useState('')
   const [note, setNote] = useState('')
   const [showConfirm, setShowConfirm] = useState(false)
   const [openConfirmPin, setOpenConfirmPin] = useState(false)
   const uploadRef = useRef<HTMLInputElement | null>(null)
   const { user: userService } = useApi()
   const [users, setUsers] = useState<UserItem[]>([])
   const [loading, setLoading] = useState(false)

   useEffect(() => {
      function onEsc(e: KeyboardEvent) { if (e.key === 'Escape') onClose() }
      if (open) window.addEventListener('keydown', onEsc)
      return () => window.removeEventListener('keydown', onEsc)
   }, [open, onClose])

   useEffect(() => {
      const searchUsers = async () => {
         setLoading(true)
         const res = await userService.searchUsers({ search: query })
         if (res.status === 'success' && res.data) {
            setUsers(res.data.map(u => ({ id: u.id, email: u.email, ...(u.username !== undefined && { username: u.username }) })))
         }
         setLoading(false)
      }
      if (openUsers) searchUsers()
   }, [query, openUsers, userService])

   if (!open) return null

   const isChecked = (u: UserItem) => selected.some(s => s.id === u.id)
   const toggle = (u: UserItem) => {
      setSelectAll(false)
      setSelected(prev => isChecked(u) ? prev.filter(x => x.id !== u.id) : [...prev, u])
   }
   const handleSelectAll = () => {
      setSelectAll(prev => !prev)
      setSelected([])
   }

   function onAmountChange(v: string) {
      const digits = v.replace(/[^0-9]/g, '')
      if (!digits) { setAmount(''); return }
      const n = parseInt(digits, 10)
      const formatted = '₦' + n.toLocaleString('en-NG')
      setAmount(formatted)
   }

   const canSend = (selected.length > 0 || selectAll) && amount.trim().length > 0
   const MAX_USER_CHIPS = 5
   const visibleUsers = selected.slice(0, MAX_USER_CHIPS)
   const extraUsers = Math.max(0, selected.length - MAX_USER_CHIPS)

   const handleSendClick = () => {
      setShowConfirm(true)
   }

   const handleConfirmSend = () => {
      setShowConfirm(false)
      setOpenConfirmPin(true)
   }

   const handlePinConfirm = async () => {
      const amountValue = parseInt(amount.replace(/[^0-9]/g, ''), 10)
      const payload: { amount: number; note: string; userIds?: string[] } = {
         amount: amountValue,
         note: note
      }
      if (!selectAll) {
         payload.userIds = selected.map(u => u.id)
      }

      try {
         const res = await userService.sendMoney(payload)
         if (res.status === 'success') {
            toast.success('Money sent successfully!')
            setSelected([])
            setSelectAll(false)
            setAmount('')
            setNote('')
            setQuery('')
         } else {
            toast.error(res.message || 'Failed to send money')
         }
      } catch (error) {
         toast.error(error instanceof Error ? error.message : 'An error occurred')
      } finally {
         onClose()
      }
   }

   return (
      <>
         <ConfirmSendMoneyModal
            open={showConfirm}
            amount={amount}
            recipientCount={selectAll ? 'All' : selected.length}
            onClose={() => setShowConfirm(false)}
            onConfirm={handleConfirmSend}
         />

         <ConfirmPinModal open={openConfirmPin} onClose={() => setOpenConfirmPin(false)} onConfirm={handlePinConfirm} />

         <Overlay onClick={onClose}>
            <Panel onClick={e => e.stopPropagation()}>
               <TitleRow>
                  <H1>Send money</H1>
                  <CloseBtn onClick={onClose}><Icon icon="material-symbols:close" width={20} /></CloseBtn>
               </TitleRow>

               {/* Users */}
               <Section>
                  <div className='flex items-center justify-between mb-2'>
                     <div className='text-[12px] tracking-tight text-[#9C9C9F]'>Users</div>
                     <button onClick={() => uploadRef.current?.click()} className='flex items-center gap-1 text-[11px] text-[#6D6D72] hover:text-[#3F3F45]'>
                        <Icon icon='solar:upload-linear' width={14} />
                        <span>Upload csv</span>
                     </button>
                     <input ref={uploadRef} type='file' accept='.csv' className='hidden' />
                  </div>

                  {/* Selected chips outside the field */}
                  <div className='flex flex-wrap gap-2 mb-2'>
                     {selectAll ? (
                        <span className='px-2 py-1 text-[11px] rounded-full bg-[#AF7AD5] text-white'>All Users</span>
                     ) : (
                        <>
                           {visibleUsers.map(u => (
                              <span key={u.id} className='px-2 py-1 text-[11px] rounded-full bg-[#F1F1F3] text-[#6D6D72]'>
                                 {u.username ?? u.email}
                              </span>
                           ))}
                           {extraUsers > 0 && (
                              <span className='px-2 py-1 text-[11px] rounded-full bg-[#F1F1F3] text-[#6D6D72]'>+{extraUsers}</span>
                           )}
                        </>
                     )}
                  </div>

                  <div className='relative'>
                     <button
                        ref={usersBtnRef}
                        onClick={() => setOpenUsers(o => !o)}
                        className='w-full h-9 px-2 rounded-md border border-[#E7E7E9] bg-white flex items-center justify-between text-sm hover:bg-[#F9F9FA]'
                     >
                        <span className='text-[#9C9C9F]'>
                           {selectAll ? 'All users selected' : selected.length ? 'Edit users' : 'Select users'}
                        </span>
                        <Icon icon='solar:alt-arrow-down-linear' width={16} className='text-[#B3B3B6]' />
                     </button>

                     {openUsers && (
                        <div className='absolute z-40 top-[42px] left-0 right-0'>
                           <UsersDropdown
                              open={openUsers}
                              onClose={() => setOpenUsers(false)}
                              anchorRef={usersBtnRef}
                              items={users}
                              query={query}
                              setQuery={setQuery}
                              isChecked={isChecked}
                              onToggle={toggle}
                              onSelectAll={handleSelectAll}
                              selectedCount={selectAll ? 'All' : selected.length}
                              loading={loading}
                              selectAllActive={selectAll}
                           />
                        </div>
                     )}
                  </div>
               </Section>

               {/* Amount */}
               <Section>
                  <div className='text-[12px] tracking-tight text-[#9C9C9F] mb-2'>Amount to send</div>
                  <input
                     value={amount}
                     onChange={e => onAmountChange(e.target.value)}
                     placeholder='₦10,000'
                     className='w-full h-9 px-3 rounded-md border border-[#E7E7E9] text-sm outline-none focus:border-[#AF7AD5] not-placeholder-shown:font-medium'
                  />
               </Section>

               {/* Note */}
               <Section>
                  <div className='text-[12px] tracking-tight text-[#9C9C9F] mb-2'>Note (Optional)</div>
                  <textarea
                     value={note}
                     onChange={e => setNote(e.target.value)}
                     placeholder='Compensation for delay...'
                     className='w-full min-h-[88px] p-3 rounded-md border border-[#E7E7E9] text-sm outline-none focus:border-[#AF7AD5]'
                  />
               </Section>

               <div className='mt-auto flex items-center justify-between'>
                  <button onClick={onClose} className='px-4 py-2 rounded-full text-sm font-semibold bg-[#F5F5F6] text-[#3F3F45] hover:bg-[#EDEDEE] cursor-pointer'>Cancel</button>
                  <button
                     onClick={handleSendClick}
                     disabled={!canSend}
                     className={`px-4 py-2 rounded-full text-sm font-semibold text-white cursor-pointer ${canSend ? 'bg-[#AF7AD5] hover:bg-[#9b63c9]' : 'bg-[#E0D2EE] cursor-not-allowed'}`}
                  >
                     Send
                  </button>
               </div>
            </Panel>
         </Overlay>
      </>
   )
}

function UsersDropdown({
   open, onClose, anchorRef, items, query, setQuery,
   isChecked, onToggle, onSelectAll, selectedCount, loading, selectAllActive
}: {
   open: boolean
   onClose: () => void
   anchorRef: React.RefObject<HTMLButtonElement | null>
   items: UserItem[]
   query: string
   setQuery: (v: string) => void
   isChecked: (u: UserItem) => boolean
   onToggle: (u: UserItem) => void
   onSelectAll: () => void
   selectedCount: number | string
   loading: boolean
   selectAllActive: boolean
}) {
   const wrapRef = useRef<HTMLDivElement | null>(null)

   useEffect(() => {
      function handle(e: MouseEvent) {
         if (!open) return
         if (wrapRef.current && !wrapRef.current.contains(e.target as Node) && anchorRef.current && !anchorRef.current.contains(e.target as Node)) onClose()
      }
      window.addEventListener('mousedown', handle)
      return () => window.removeEventListener('mousedown', handle)
   }, [open, onClose, anchorRef])

   return (
      <div ref={wrapRef} className='bg-white border border-[#EDEDEE] rounded-lg shadow-[0_4px_6px_-2px_rgba(16,24,40,0.04),0_14px_16px_-2px_rgba(16,24,40,0.08)] p-2'>
         {/* Search */}
         <div className='px-2 py-2'>
            <div className='flex items-center gap-2 px-2 py-2 border border-[#EDEDEE] rounded-md'>
               <Icon icon='solar:magnifer-linear' width={14} className='text-[#CACACC]' />
               <input value={query} onChange={e => setQuery(e.target.value)} placeholder='Search by username or email' className='flex-1 text-sm outline-none' />
            </div>
            <div className='flex items-center justify-between mt-2 text-[11px] text-[#9C9C9F]'>
               <span>Active users</span>
               <span className='flex items-center gap-1'>Sort: A-Z <Icon icon='solar:alt-arrow-down-linear' width={14} /></span>
            </div>
         </div>

         {/* List */}
         <div className='max-h-56 overflow-auto'>
            {loading ? (
               <div className='px-3 py-6 flex justify-center'>
                  <Icon icon='svg-spinners:ring-resize' width={24} className='text-[#AF7AD5]' />
               </div>
            ) : (
               <>
                  {items.map(u => (
                     <label key={u.id} className='flex items-center gap-3 px-2 py-2 hover:bg-[#F9F9FA] cursor-pointer text-sm'>
                        <input type='checkbox' checked={isChecked(u)} onChange={() => onToggle(u)} className='accent-[#AF7AD5]' disabled={selectAllActive} />
                        <span className='truncate'>{u.email}</span>
                        <span className='ml-auto px-2 py-0.5 rounded-full bg-[#F1F1F3] text-[#6D6D72] text-[11px]'>@{u.username ?? 'N/A'}</span>
                     </label>
                  ))}
                  {items.length === 0 && !loading && (
                     <div className='px-3 py-6 text-xs text-[#9C9C9F]'>No users found.</div>
                  )}
               </>
            )}
         </div>

         <div className='flex items-center justify-between px-2 py-2 border-t border-[#F5F5F6] text-[12px]'>
            <div className='flex items-center gap-2 text-[#B3B3B6]'>
               <Icon icon='solar:checklist-linear' width={14} />
               <span>{selectedCount} Users selected</span>
            </div>
            <button onClick={onSelectAll} className='px-3 py-1 text-xs rounded-md border border-[#E7E7E9] bg-white hover:bg-[#F9F9FA]'>{selectAllActive ? 'Remove all' : 'Select all'}</button>
         </div>
      </div>
   )
}
